import { useTranslation } from "react-i18next";
import heroStyles from "../consumption/UsageHero.module.css";
import styles from "./Overview.module.css";
import { formatRoundedMoney } from "../consumption/formatters";
import { WorthALook, type OrganisationInsight } from "../organisation/WorthALook";
import { BudgetGauge } from "./BudgetGauge";
import { monthLabel, type BudgetPeriod, type CapexSplit, type FinancialYearComparison, type OverviewHeadline } from "./overviewFigures";

/**
 * The top of the Organization page, in the My Usage hero's look: the month's spend and where it is heading against the month
 * before and against the budget, then the few things worth a look, with the people using AI, how many came back, the year so far
 * and how concentrated the spend is beside it.
 *
 * @param props.headline - The headline figures.
 * @param props.financialYear - This financial year so far against the last, or null.
 * @param props.budget - This month and this year against the (assumed) budget, or null when there is none.
 * @param props.currency - The currency the spend is in.
 * @param props.capex - How the month's spend divides into Opex and Capex, given only while Capex is not counted against budgets.
 * @param props.insights - The things worth a look, most important first; the list is left out when there are none.
 */
export function OverviewHero({
  headline,
  financialYear,
  budget,
  currency,
  capex = null,
  insights = [],
}: {
  headline: OverviewHeadline;
  financialYear: FinancialYearComparison | null;
  budget: { month: BudgetPeriod; year: BudgetPeriod | null } | null;
  currency: string;
  capex?: CapexSplit | null;
  insights?: OrganisationInsight[];
}): React.JSX.Element {
  const describe = (period: BudgetPeriod, key: "overview.hero.budgetMonthLabel" | "overview.hero.budgetYearLabel", name: string): string =>
    t(key, {
      month: name,
      name,
      spent: formatRoundedMoney(period.spent, currency),
      budget: formatRoundedMoney(period.budget, currency),
      percent: Math.round((period.spent / period.budget) * 100),
      elapsed: Math.round(period.elapsedPercent),
      projection: period.projected === null ? "" : t("overview.hero.budgetProjection", { projected: formatRoundedMoney(period.projected, currency) }),
    });
  const { t } = useTranslation();
  const { month, previous } = headline;
  const monthName = new Date(`${month.periodStart}T00:00:00Z`).toLocaleDateString("en-US", { month: "long", year: "numeric", timeZone: "UTC" });
  const previousName = previous ? new Date(`${previous.periodStart}T00:00:00Z`).toLocaleDateString("en-US", { month: "long", timeZone: "UTC" }) : "";
  const change = headline.changePercent;
  const direction = change !== null && change < 0 ? t("overview.hero.less") : t("overview.hero.more");
  const percent = change !== null ? String(Math.round(Math.abs(change))) : "";
  const peopleChange = previous ? month.activePeople - previous.activePeople : null;

  const summary = month.isCurrentMonth
    ? headline.projected === null
      ? null
      : change === null
        ? t("overview.hero.runningSummary", { projected: formatRoundedMoney(headline.projected, currency) })
        : t("overview.hero.runningChange", { projected: formatRoundedMoney(headline.projected, currency), percent, direction, previous: previousName })
    : change === null
      ? t("overview.hero.pastSummary", { spend: formatRoundedMoney(headline.spend, currency), month: monthName })
      : t("overview.hero.pastChange", { spend: formatRoundedMoney(headline.spend, currency), month: monthName, percent, direction, previous: previousName });

  return (
    <section className={heroStyles.hero} data-spotlight aria-label={t("overview.hero.label")}>
      <div className={heroStyles.main}>
        <div className={heroStyles.headerRow}>
          <p className={`panel-title ${heroStyles.eyebrow}`}>{t("overview.hero.eyebrow")}</p>
          <p className={heroStyles.periodRange}>{monthName}</p>
        </div>
        <p className={heroStyles.amount}>
          <span>{formatRoundedMoney(headline.spend, currency)}</span>
          {month.isCurrentMonth && <span className={heroStyles.allowanceTotal}>{t("overview.hero.soFar")}</span>}
        </p>
        {summary && <p className={heroStyles.summary}>{summary}</p>}
        {budget && (
          <div className={styles.budget} aria-label={t("overview.hero.budgetLabel")} role="group">
            <div className={styles.gauges}>
              <BudgetGauge
                period={budget.month}
                title={monthName.split(" ")[0]}
                caption={t("overview.hero.budgetCaption", { amount: formatRoundedMoney(budget.month.budget, currency) })}
                label={describe(budget.month, "overview.hero.budgetMonthLabel", monthName)}
              />
              {budget.year && financialYear && (
                <BudgetGauge
                  period={budget.year}
                  title={financialYear.name}
                  caption={t("overview.hero.budgetCaption", { amount: formatRoundedMoney(budget.year.budget, currency) })}
                  label={describe(budget.year, "overview.hero.budgetYearLabel", financialYear.name)}
                />
              )}
            </div>
            <div className={styles.budgetKey} aria-hidden="true">
              <span className={styles.keyItem}>
                <span className={`${styles.keySwatch} ${styles.keySpent}`} />
                {t("overview.hero.budgetKeySpent")}
              </span>
              <span className={styles.keyItem}>
                <span className={`${styles.keySwatch} ${styles.keyProjected}`} />
                {t("overview.hero.budgetKeyProjected")}
              </span>
              <span className={styles.keyItem}>
                <span className={styles.keyTick} />
                {t("overview.hero.budgetKeyElapsed")}
              </span>
            </div>
            <p className={heroStyles.note}>{t("overview.hero.budgetRecorded", { amount: formatRoundedMoney(budget.month.budget, currency) })}</p>
          </div>
        )}
        {!month.wasFullyConverted && <p className={heroStyles.note}>{t("overview.hero.notFullyConverted")}</p>}
        {financialYear && financialYear.incompleteFrom.length > 0 && financialYear.previousSamePoint !== null && (
          <p className={heroStyles.note}>
            {t("overview.hero.financialYearIncomplete", {
              previous: financialYear.previousName,
              list: financialYear.incompleteFrom
                .map((start) => t("overview.hero.financialYearIncompleteItem", { tool: start.displayName, month: monthLabel(start.periodStart, true) }))
                .join(", "),
            })}
          </p>
        )}
      </div>

      <dl className={heroStyles.stats} aria-label={t("overview.hero.statsLabel")}>
        <div className={`${heroStyles.stat} ${heroStyles.statFeatured}`}>
          <dt>{t("overview.hero.people")}</dt>
          <dd>
            {month.activePeople}
            {peopleChange !== null && (
              <span className={heroStyles.statNote}>
                {t("overview.hero.peopleChange", { change: `${peopleChange >= 0 ? "+" : "−"}${String(Math.abs(peopleChange))}`, previous: previousName })}
              </span>
            )}
          </dd>
        </div>
        {headline.returningPercent !== null && (
          <div className={heroStyles.stat}>
            <dt>{t("overview.hero.returning", { previous: previousName })}</dt>
            <dd>
              {`${String(Math.round(headline.returningPercent))}%`}
              <span className={heroStyles.statNote}>{t("overview.hero.returningNote", { previous: previousName })}</span>
            </dd>
          </div>
        )}
        {month.topTenthSharePercent !== null && (
          <div className={heroStyles.stat}>
            <dt>{t("overview.hero.topTenth")}</dt>
            <dd>
              {`${String(Math.round(month.topTenthSharePercent))}%`}
              <span className={heroStyles.statNote}>{t("overview.hero.topTenthNote")}</span>
            </dd>
          </div>
        )}
        {capex && (
          <div className={`${heroStyles.stat} ${styles.statFull}`}>
            <dt>{t("overview.hero.capex")}</dt>
            <dd>
              {formatRoundedMoney(capex.capexUsd, currency)}
              <span className={heroStyles.statNote}>{t("overview.hero.capexNote", { opex: formatRoundedMoney(capex.opexUsd, currency) })}</span>
            </dd>
          </div>
        )}
        {financialYear && (
          <div className={`${heroStyles.stat} ${styles.statFull}`}>
            <dt>{t("overview.hero.financialYear", { name: financialYear.name })}</dt>
            <dd>
              {formatRoundedMoney(financialYear.soFar, currency)}
              {financialYear.previousSamePoint !== null && (
                <span className={heroStyles.statNote}>
                  {financialYear.changePercent === null
                    ? t("overview.hero.financialYearPrevious", { amount: formatRoundedMoney(financialYear.previousSamePoint, currency), previous: financialYear.previousName })
                    : t("overview.hero.financialYearChange", {
                        percent: Math.round(Math.abs(financialYear.changePercent)),
                        direction: financialYear.changePercent < 0 ? t("overview.hero.less") : t("overview.hero.more"),
                        previous: financialYear.previousName,
                        amount: formatRoundedMoney(financialYear.previousSamePoint, currency),
                      })}
                </span>
              )}
              {financialYear.likeForLike && (
                <span className={heroStyles.statNote}>
                  {t("overview.hero.financialYearLikeForLike", {
                    tools: financialYear.likeForLike.tools.join(", "),
                    percent: Math.round(Math.abs(financialYear.likeForLike.changePercent)),
                    direction: financialYear.likeForLike.changePercent < 0 ? t("overview.hero.less") : t("overview.hero.more"),
                    previous: financialYear.previousName,
                  })}
                </span>
              )}
              {financialYear.previousTotal !== null && (
                <span className={heroStyles.statNote}>
                  {t("overview.hero.financialYearTotal", { previous: financialYear.previousName, amount: formatRoundedMoney(financialYear.previousTotal, currency) })}
                </span>
              )}
            </dd>
          </div>
        )}
      </dl>
      {/* Beneath both columns, across the hero, so its rows have room for their notes. */}
      {insights.length > 0 && <WorthALook insights={insights} className={styles.worthALook} />}
    </section>
  );
}
